import { useAuthentication, EXTERNAL_LINKS, SUPPORT_EMAIL } from '@psyomics/ui-common';
import { BackLink, Button, GenericError, Header, LoadingSpinner, PageLink, Text } from '@psyomics/ui-components';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ServiceUserDetail } from '../generated/service-user';
import { useGetServiceUserQuery } from '../hooks/use-get-service-user-query';

const useAccountData = () => {
  const { logout, initiateChangePassword } = useAuthentication();

  const [serviceUser, fetching] = useGetServiceUserQuery();

  return {
    serviceUser,
    fetching,
    logout,
    initiateChangePassword,
  };
};

const UserInfo = ({
  serviceUser,
  className,
}: {
  readonly serviceUser: ServiceUserDetail;
  readonly className?: string;
}) => (
  <div className={className}>
    <span>{serviceUser.username}</span>
  </div>
);

const ActionButtons = ({
  onChangePassword,
  onSignOut,
  signOutText,
  changePasswordText,
  allowChangePassword,
}: {
  readonly onChangePassword: () => void;
  readonly onSignOut: () => void;
  readonly signOutText: string;
  readonly changePasswordText: string;
  readonly allowChangePassword: boolean;
}) => {
  return (
    <div>
      <div className="self-stretch inline-flex flex-col justify-start items-start gap-3 w-full">
        <Button appearance="secondary" className="w-full" onClick={() => onSignOut()}>
          {signOutText}
        </Button>
        {allowChangePassword && (
          <Button appearance="secondary" className="w-full" onClick={onChangePassword}>
            {changePasswordText}
          </Button>
        )}
      </div>
    </div>
  );
};

const NavigationLinks = ({
  aboutLinkText,
  termsLinkText,
  privacyLinkText,
}: {
  readonly aboutLinkText: string;
  readonly termsLinkText: string;
  readonly privacyLinkText: string;
}) => {
  return (
    <>
      <PageLink text={aboutLinkText} to="/about" />
      <PageLink text={termsLinkText} to={EXTERNAL_LINKS.TERMS_OF_USE} />
      <PageLink text={privacyLinkText} to={EXTERNAL_LINKS.PRIVACY_POLICY} />
    </>
  );
};

const Account = () => {
  const { serviceUser, fetching, logout } = useAccountData();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleChangePassword = () => {
    navigate('/account/change-password');
  };

  if (fetching) return <LoadingSpinner />;

  if (!serviceUser) return <GenericError titleKey={'page.account.userNotFound'} />;

  return (
    <div className="relative flex justify-center">
      <div className="max-w-[680px] w-full mx-auto h-full pt-0 p-6">
        <div>
          <BackLink onBack={() => navigate('/')} />
        </div>
        <Header className="pb-6" level="h2">
          {t('page.account.title')}
        </Header>
        <UserInfo className="pb-2" serviceUser={serviceUser} />
        <ActionButtons
          allowChangePassword={serviceUser.hasTrustReferral}
          changePasswordText={t('page.account.changePassword')}
          onChangePassword={handleChangePassword}
          onSignOut={logout}
          signOutText={t('page.account.signOut')}
        />
        <NavigationLinks
          aboutLinkText={t('page.account.aboutLink')}
          privacyLinkText={t('page.account.privacyLink')}
          termsLinkText={t('page.account.termsLink')}
        />
        <Text className="mt-8" size="lg" tag="p">
          <Trans
            components={{
              linkTag: (
                <a className="text-blue-500 underline" href={`mailto:${SUPPORT_EMAIL}`}>
                  {SUPPORT_EMAIL}
                </a>
              ),
            }}
            i18nKey="page.account.support"
          />
        </Text>
      </div>
    </div>
  );
};

export { Account };
